import { FC } from 'react';
import { useIntl, FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import { useFragment, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { TextArea } from 'dibs-elements/exports/TextArea';
import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { updateGiftMessage, setIsGiftSelected } from '../actions/checkoutUIActions';
import Gift from 'dibs-icons/exports/legacy/Gift';

import styles from './styles/CheckoutGifting.scss';

import { CheckoutGifting_item$key } from './__generated__/CheckoutGifting_item.graphql';
import { CheckoutGifting_transaction$key } from './__generated__/CheckoutGifting_transaction.graphql';

type Props = {
    hasSelectedGift: boolean;
    onSetIsGiftSelected: (arg1: boolean) => void;
    giftNotes: string;
    onUpdateGiftMessage: (arg1: string) => void;
    isComputing: boolean;
    item: CheckoutGifting_item$key;
    transaction: CheckoutGifting_transaction$key;
    isMobile: boolean;
};

const CheckoutGifting: FC<Props> = props => {
    const intl = useIntl();
    const {
        item: itemRef,
        transaction: transactionRef,
        onUpdateGiftMessage,
        giftNotes,
        hasSelectedGift,
        onSetIsGiftSelected,
        isComputing,
        isMobile,
    } = props;

    const item = useFragment(
        graphql`
            fragment CheckoutGifting_item on Item {
                vertical
            }
        `,
        itemRef
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutGifting_transaction on Transaction {
                isGiftEligible
            }
        `,
        transactionRef
    );

    const vertical = item?.vertical;
    const isGiftEligible = transaction?.isGiftEligible;
    const ordeHasBecomeIneligibleForGift = !isGiftEligible && hasSelectedGift;

    if (!isGiftEligible && !hasSelectedGift) {
        return null;
    }

    return (
        <div
            className={classnames(styles.container, {
                [styles.mobileContainer]: isMobile,
            })}
        >
            {' '}
            <div className={styles.checkboxContainer}>
                <Checkbox
                    name="giftCheckbox"
                    label={
                        <span>
                            <FormattedMessage
                                id="checkout.CheckoutGifting.isGiftCaption"
                                defaultMessage="This order is a gift"
                            />
                        </span>
                    }
                    checked={hasSelectedGift && !ordeHasBecomeIneligibleForGift}
                    disabled={isComputing || ordeHasBecomeIneligibleForGift}
                    wrapperClass={styles.checkbox}
                    onChange={() => {
                        onSetIsGiftSelected(!hasSelectedGift);
                    }}
                    dataTn="gift-checkbox"
                />
                <Gift className={styles.icon} />
            </div>
            {ordeHasBecomeIneligibleForGift && (
                <div className={styles.ineligibleCaption}>
                    <FormattedMessage
                        id="checkout.CheckoutGifting.CheckoutGiftingBecameInegligibleCaption"
                        defaultMessage="Unfortunately, gift wrapping is not available on international shipping."
                    />
                </div>
            )}
            {hasSelectedGift && !ordeHasBecomeIneligibleForGift && (
                <>
                    <div className={styles.giftingCaptionContainer}>
                        <FormattedMessage
                            id="checkout.CheckoutGifting.CheckoutGiftingInfoCaption"
                            defaultMessage="{vertical, select,
                                furniture {The seller offers gift messaging for the recipient. Prices won't be shown on the packing slip.}
                                art {The seller offers gift messaging for the recipient. Prices won't be shown on the packing slip.}
                                other {The seller provides complimentary gift wrapping, and prices won't be shown on the packing slip. You can also add a gift message to the recipient.}
                            }"
                            values={{ vertical }}
                        />
                    </div>
                    <TextArea
                        ariaLabel={intl.formatMessage({
                            id: 'BuyerGiftMessage.textAreaLabel',
                            defaultMessage: 'Gift Message',
                        })}
                        name="textArea"
                        onChange={event => onUpdateGiftMessage(event.target.value)}
                        disabled={isComputing}
                        dataTn="buyer-gift-message"
                        rows={4}
                        value={giftNotes || ''}
                        maxLength={140}
                    />
                </>
            )}
        </div>
    );
};

type ReduxPayload = {
    transaction: {
        giftNotes: string;
        hasSelectedGift: boolean;
    };
};

const ConnectedCheckoutGifting = connect(
    ({ transaction }: ReduxPayload) => ({
        giftNotes: transaction.giftNotes,
        hasSelectedGift: transaction.hasSelectedGift,
    }),
    {
        onUpdateGiftMessage: updateGiftMessage,
        onSetIsGiftSelected: setIsGiftSelected,
    }
)(CheckoutGifting);

export default ConnectedCheckoutGifting;
